import type { ParentDraftSchema } from '@repo/schemas/parent/parentDraft';
import type { UserPrivate, UserPublic } from '@repo/types/users';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useGetStudentParents } from '@repo/api/parent/useGetStudentParents';
import { useLinkParent } from '@repo/api/parent/useLinkParent';
import { useUnlinkParent } from '@repo/api/parent/useUnlinkParent';
import { usePostUser } from '@repo/api/user/usePostUser';
import { generatePassword } from '@repo/lib/generatePassword';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { userErrorMessages } from '@repo/lib/messages/userErrorMessages';
import { parentDraftSchema } from '@repo/schemas/parent/parentDraft';
import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { UserIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { getAvatar } from '@/utils/getAvatar';

import type { NewParentDraft } from './CreateUserParentSection';

import { CreatedParentAccounts } from './CreatedParentAccounts';
import { ParentDraftForm } from './ParentDraftForm';
import { ParentLinkPicker } from './ParentLinkPicker';

interface EditUserParentSectionProps {
  studentId: number;
  parents: UserPrivate[];
  onOpenParent: (parent: UserPublic) => void;
}

export function EditUserParentSection({ studentId, parents, onOpenParent }: EditUserParentSectionProps) {
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [draftFormOpen, setDraftFormOpen] = useState(false);
  const [createdParents, setCreatedParents] = useState<NewParentDraft[]>([]);

  const { data: linkedParents = [] } = useGetStudentParents(studentId);
  const { mutateAsync: linkParent } = useLinkParent();
  const { mutateAsync: unlinkParent } = useUnlinkParent();
  const { mutateAsync: postUser, isPending: isCreating } = usePostUser();

  const linkedParentIds = useMemo(() => new Set(linkedParents.map((parent: UserPublic) => parent.id)), [linkedParents]);
  const availableParents = useMemo(() => parents.filter((parent) => parent.id !== studentId && !linkedParentIds.has(parent.id)), [parents, linkedParentIds, studentId]);

  const draftForm = useForm<ParentDraftSchema>({
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: null,
      birthdate: null,
    },
    mode: 'onBlur',
    resolver: standardSchemaResolver(parentDraftSchema),
  });

  const handleLinkExisting = (parent: UserPrivate) => {
    setParentPickerOpen(false);
    linkParent({ parentId: parent.id, studentId }).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  const handleUnlink = (parentId: number) => {
    unlinkParent({ parentId, studentId }).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  const onAddDraft = async (draft: ParentDraftSchema) => {
    const password = generatePassword(10);
    try {
      const parent = await postUser({
        ...draft,
        lvl: UserLvl.STUDENT,
        password,
        accountType: AccountType.Parent,
      });
      await linkParent({ parentId: parent.id, studentId });
      setCreatedParents((prev) => [...prev, { ...draft, password }]);
      draftForm.reset();
      setDraftFormOpen(false);
    } catch (error) {
      toasterMessageHandler(userErrorMessages, error instanceof Error ? error : null);
    }
  };

  const onCancelDraft = () => {
    draftForm.reset();
    setDraftFormOpen(false);
  };

  return (
    <div className={'space-y-3 pt-3'}>
      <h5 className={'font-medium'}>Powiązani rodzice:</h5>
      <div className={'flex flex-col gap-2'}>
        {linkedParents.length === 0 && !draftFormOpen && (
          <EmptyState className={'border-border-secondary bg-muted/25 w-full rounded-md border pt-4!'}>
            <EmptyStateContent>
              <EmptyStateTitle className={'text-sm!'}>Brak powiązanych rodziców</EmptyStateTitle>
              <EmptyStateDescription className={'max-w-xs text-xs!'}>Powiąż istniejącego rodzica lub utwórz nowego.</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}
        {linkedParents.length > 0 && (
          <ul className={'flex flex-col gap-2'}>
            {linkedParents.map((parent: UserPublic) => (
              <li key={parent.id} className={'border-border-primary bg-card/75 flex items-center gap-3 rounded-md border p-2'}>
                <button
                  type={'button'}
                  onClick={() => onOpenParent(parent)}
                  className={'hover:bg-muted/40 -m-1 flex min-w-0 grow items-center gap-3 rounded-md p-1 text-left transition-colors'}
                  aria-label={`Otwórz rodzica ${parent.name} ${parent.surname}`}
                >
                  <Avatar size={'sm'}>
                    <AvatarImage src={parent.avatarURL ? getAvatar(parent.avatarURL) : undefined} />
                    <AvatarFallback>
                      <UserIcon className={'size-3.5'} />
                    </AvatarFallback>
                  </Avatar>
                  <div className={'flex min-w-0 grow flex-col'}>
                    <p className={'truncate text-sm'}>
                      {parent.name} {parent.surname}
                    </p>
                    <p className={'text-text-tertiary truncate text-xs'}>{parent.email}</p>
                  </div>
                </button>
                <Button size={'sm'} variant={'ghost'} variantColor={'destructive'} onClick={() => handleUnlink(parent.id)} aria-label={`Odepnij rodzica ${parent.name} ${parent.surname}`}>
                  <XIcon />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {draftFormOpen && <ParentDraftForm form={draftForm} onSubmit={onAddDraft} onCancel={onCancelDraft} isSubmitting={isCreating} />}
        <ParentLinkPicker
          availableParents={availableParents}
          open={parentPickerOpen}
          onOpenChange={setParentPickerOpen}
          onSelectParent={handleLinkExisting}
          onCreateNewParent={() => setDraftFormOpen(true)}
        />
        <CreatedParentAccounts parents={createdParents} />
      </div>
    </div>
  );
}
